import React, {
    useCallback,
    useId,
    useEffect,
    useState,
    useRef,
    useMemo,
} from "react";
import { flushSync } from "react-dom";
import { modifyQueryParameters } from "@bloomberg/media-ui-core";

import { useTranslateMessage } from "~i18n/translate";
import GoogleAnalyticsEventTracker from "~utils/GoogleAnalyticsEventTracker";

type ThirdPartyEmbedProps = {
    embedHeight?: string | number;
    embedUrl: string;
    embedTitle?: string;
    additionalQueryParams: Record<string, string>;
};

type EventData = { height: number; method: string; windowId: string };

type PostMessageHandshakeParams = {
    iframe: HTMLIFrameElement | null;
    windowId: string;
    // Optional: restrict origin for security
    messageOrigin?: string;
    getMessageData?: (event: MessageEvent<EventData>) => EventData | null;
    isMatchingMessage?: (data: EventData) => boolean;
    handleMessage: (data: EventData) => void;
};

const getNormalizedEventData = (event: MessageEvent) => {
    if (typeof event.data !== "string") return null;

    try {
        return JSON.parse(event.data);
    } catch {
        console.warn(
            "[ThirdPartyEmbed] Stringified event data cannot be converted to JSON",
            event.data
        );
    }

    return null;
};

const getMessageDataDefault = (event: MessageEvent) => event.data;
const isMatchingMessageDefault = (data: EventData) =>
    data?.method === "resize" &&
    typeof data.windowId === "number" &&
    typeof data.height === "number";

export function usePostMessageHandshake({
    iframe,
    windowId,
    messageOrigin = "*",
    getMessageData = getMessageDataDefault,
    isMatchingMessage = isMatchingMessageDefault,
    handleMessage,
}: PostMessageHandshakeParams) {
    const isEmbedReady = useRef(false);

    // Listen for matching embed message events
    const handleEmbedMessage = useCallback(
        (event: MessageEvent) => {
            if (!isEmbedReady.current) return;

            if (messageOrigin !== "*" && event.origin !== messageOrigin) return;

            const data = getMessageData(event);

            if (data && isMatchingMessage(data)) {
                console.debug(
                    "[ThirdPartyEmbed] resize event received",
                    windowId
                );
                handleMessage(data);
            }
        },
        [iframe, getMessageData, isMatchingMessage]
    );

    // Listen for the embed "ready" event
    const handleReadyMessage = useCallback(
        (event: MessageEvent) => {
            if (!iframe) return;

            isEmbedReady.current = true;

            if (event.data?.method === "embed:ready") {
                console.debug(
                    "[ThirdPartyEmbed] embed:ready event received",
                    windowId
                );

                const iframeWindow = iframe.contentWindow;
                iframeWindow?.postMessage(
                    {
                        method: "register",
                        windowId,
                    },
                    "*"
                );
            }
        },
        [iframe]
    );

    // For backwards compatibility: listen for the iframe load event
    const handleIframeLoadEvent = useCallback(() => {
        if (!iframe) return;

        isEmbedReady.current = true;
        console.debug("[ThirdPartyEmbed] iframe load event received", windowId);

        const iframeWindow = iframe.contentWindow;
        iframeWindow?.postMessage(
            JSON.stringify({
                method: "register",
                windowId,
            }),
            "*"
        );
    }, [iframe]);

    useEffect(() => {
        if (!iframe) return () => {};

        window.addEventListener("message", handleReadyMessage);
        window.addEventListener("message", handleEmbedMessage);
        iframe.addEventListener("load", handleIframeLoadEvent);
        return () => {
            window.removeEventListener("message", handleReadyMessage);
            window.removeEventListener("message", handleEmbedMessage);
            iframe.removeEventListener("load", handleIframeLoadEvent);
        };
    }, [iframe, handleEmbedMessage, handleReadyMessage, handleIframeLoadEvent]);
}

export default function ThirdPartyEmbed(props: ThirdPartyEmbedProps) {
    const { embedHeight, embedTitle, embedUrl, additionalQueryParams } = props;

    const [height, setHeight] = useState<string>(
        embedHeight?.toString() || "100%"
    );
    const [iframeRef, setIframeRef] = useState<HTMLIFrameElement | null>(null);

    const windowId = useId();

    const translateMessage = useTranslateMessage();
    const title =
        embedTitle ||
        translateMessage({
            id: "thirdPartyEmbedIframe",
            defaultMessage: "Third party embed iframe",
        });

    const setIframeRefOnNodeChange = useCallback(
        (node: HTMLIFrameElement | null) => {
            if (iframeRef !== node) {
                setIframeRef(node);
            }
        },
        [setIframeRef, iframeRef]
    );

    usePostMessageHandshake({
        // Note: iframeRef is a state variable and not technically a ref
        iframe: iframeRef,
        windowId,
        messageOrigin: "*",
        isMatchingMessage: (data: EventData) =>
            typeof data === "object" &&
            data.method === "resize" &&
            typeof data.height === "number" &&
            data.windowId === windowId,
        getMessageData: (event: MessageEvent) => getNormalizedEventData(event),
        handleMessage: (data: EventData) => {
            flushSync(() => setHeight(data?.height.toString()));
        },
    });

    const tracker = useMemo(
        () =>
            new GoogleAnalyticsEventTracker({
                event: "lineup_module_click",
            }),
        []
    );

    useEffect(() => {
        if (!embedUrl || !iframeRef) {
            return;
        }

        if (embedUrl.startsWith(window.location.origin)) {
            // Same origin case, we can attach click events inside the iframe
            iframeRef.onload = () => {
                const iframeDocument =
                    iframeRef.contentDocument ||
                    iframeRef.contentWindow?.document;
                iframeDocument?.addEventListener("click", () =>
                    tracker.trackEvent({
                        category: "engagement",
                        action: "click",
                        label: embedTitle || "third_party_embed",
                    })
                );
            };
        } else {
            // Not same origin, proxy tracking doesn't work
        }
    }, [embedUrl, iframeRef]);

    if (!embedUrl) return null;

    const modifiedUrl = modifyQueryParameters(embedUrl, additionalQueryParams);

    return (
        <section data-component="third-party-embed" className="h-full">
            <iframe
                // This is not really reliable
                // And should be replaced with the module id
                key={windowId}
                title={title}
                src={modifiedUrl}
                allowFullScreen
                frameBorder="0"
                scrolling="no"
                width="100%"
                height={height}
                data-testid="iframe"
                data-embedid={windowId}
                ref={setIframeRefOnNodeChange}
            />
        </section>
    );
}
